import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useApp } from '../context/AppContext';
import { EventBooking } from '../types';
import { apiService } from '../services/apiService';
import { formatDateTime } from '../utils/format';
import {
  CalendarCheck,
  Users,
  Clock,
  Mail,
  Phone,
  Search,
  CheckCircle2,
  AlertCircle,
  XCircle,
  PartyPopper,
  Briefcase,
  Utensils,
  Home,
  Wine,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  Edit3,
  MessageSquare,
  Sparkles,
  Download
} from 'lucide-react';

export const BookingsManagementStudio: React.FC = () => {
  const { lang } = useLanguage();
  const { restaurantProfile } = useApp();

  const [bookings, setBookings] = useState<EventBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'contacted' | 'confirmed' | 'declined'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedBookingForEmail, setSelectedBookingForEmail] = useState<EventBooking | null>(null);

  // New manual booking form
  const [newBooking, setNewBooking] = useState({
    fullName: '',
    email: '',
    phone: '',
    eventType: 'corporate' as EventBooking['eventType'],
    guests: 10,
    preferredDate: '',
    message: '',
    staffNotes: ''
  });

  const loadBookings = async () => {
    setLoading(true);
    try {
      const data = await apiService.getEventBookings();
      setBookings(data);
    } catch (e) {
      console.error('Failed to load bookings:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const handleStatusChange = async (id: string, newStatus: EventBooking['status']) => {
    await apiService.updateEventBookingStatus(id, newStatus);
    setBookings(prev => prev.map(b => (b.id === id ? { ...b, status: newStatus } : b)));
  };

  const handleUpdateNotes = async (id: string, notes: string) => {
    await apiService.updateEventBookingStatus(id, bookings.find(b => b.id === id)?.status || 'pending', notes);
    setBookings(prev => prev.map(b => (b.id === id ? { ...b, staffNotes: notes } : b)));
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(lang === 'fi' ? 'Haluatko varmasti poistaa tämän varauskyselyn?' : 'Are you sure you want to delete this event enquiry?')) {
      await apiService.deleteEventBooking(id);
      setBookings(prev => prev.filter(b => b.id !== id));
    }
  };

  const handleCreateBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    const created: EventBooking = {
      id: `evt_${Date.now()}`,
      fullName: newBooking.fullName.trim(),
      email: newBooking.email.trim(),
      phone: newBooking.phone.trim(),
      eventType: newBooking.eventType,
      guests: Number(newBooking.guests) || 4,
      preferredDate: newBooking.preferredDate,
      message: newBooking.message.trim(),
      status: 'confirmed',
      staffNotes: newBooking.staffNotes.trim() || 'Created directly by manager',
      submittedAt: new Date().toISOString()
    };

    await apiService.saveEventBooking(created);
    setBookings(prev => [created, ...prev]);
    setIsAddModalOpen(false);
    setNewBooking({
      fullName: '',
      email: '',
      phone: '',
      eventType: 'corporate',
      guests: 10,
      preferredDate: '',
      message: '',
      staffNotes: ''
    });
  };

  // Generate quick reply mailto link
  const openEmailReply = (booking: EventBooking) => {
    const subject = encodeURIComponent(`The Oven Story Helsinki - Yksityistilaisuus & Pöytävaraus (${booking.preferredDate})`);
    const body = encodeURIComponent(
      `Hei ${booking.fullName},\n\nKiitos yhteydenotostasi koskien yksityistilaisuutta ravintolassamme The Oven Story (Aleksanterinkatu 18, Helsinki).\n\nOlemme vastaanottaneet varauskyselynne päivämäärälle ${booking.preferredDate} (${booking.guests} henkilöä).\n\nHaluaisimme vahvistaa yksityiskohdat ja räätälöidä menukokonaisuuden (puu-uunipizzat, juomapaketit) toiveidenne mukaan.\n\nVoitteko vahvistaa sopiiko teille alustava aikataulu?\n\nYstävällisin terveisin,\nRavintolapäällikkö / The Oven Story Helsinki\nPuh: ${restaurantProfile.phone || '+358 40 555 8920'}\nEmail: ${restaurantProfile.email || 'asiakaspalvelu@theovenstory.fi'}\nhttps://theovenstory.fi`
    );
    window.location.href = `mailto:${booking.email}?subject=${subject}&body=${body}`;
  };

  // Export CSV
  const exportCSV = () => {
    const headers = ['ID', 'Nimi', 'Email', 'Puhelin', 'Tyyppi', 'Henkilomaara', 'Paivamaara', 'Tila', 'Viesti', 'Muistiinpanot', 'Aikaleima'];
    const rows = bookings.map(b => [
      b.id,
      `"${b.fullName}"`,
      b.email,
      b.phone,
      b.eventType,
      b.guests,
      b.preferredDate,
      b.status,
      `"${(b.message || '').replace(/"/g, '""')}"`,
      `"${(b.staffNotes || '').replace(/"/g, '""')}"`,
      b.submittedAt
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `ovenstory_bookings_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filtering
  const filteredBookings = bookings.filter(b => {
    const matchesStatus = statusFilter === 'all' || b.status === statusFilter;
    const matchesType = typeFilter === 'all' || b.eventType === typeFilter;
    const query = searchQuery.toLowerCase().trim();
    const matchesQuery =
      !query ||
      b.fullName.toLowerCase().includes(query) ||
      b.email.toLowerCase().includes(query) ||
      b.phone.toLowerCase().includes(query) ||
      (b.message && b.message.toLowerCase().includes(query)) ||
      (b.staffNotes && b.staffNotes.toLowerCase().includes(query));
    return matchesStatus && matchesType && matchesQuery;
  });

  // Key metrics
  const totalCount = bookings.length;
  const pendingCount = bookings.filter(b => b.status === 'pending').length;
  const contactedCount = bookings.filter(b => b.status === 'contacted').length;
  const confirmedCount = bookings.filter(b => b.status === 'confirmed').length;
  const totalExpectedGuests = bookings
    .filter(b => b.status === 'confirmed' || b.status === 'contacted')
    .reduce((sum, b) => sum + (Number(b.guests) || 0), 0);

  const getEventTypeLabel = (type: string) => {
    switch (type) {
      case 'birthday':
        return { label: lang === 'fi' ? 'Syntymäpäivä' : 'Birthday Party', icon: <PartyPopper size={14} color="#f59e0b" /> };
      case 'corporate':
        return { label: lang === 'fi' ? 'Yritysjuhla' : 'Corporate Event', icon: <Briefcase size={14} color="#3b82f6" /> };
      case 'team':
        return { label: lang === 'fi' ? 'Tiimi-ilta' : 'Team Dinner', icon: <Utensils size={14} color="#10b981" /> };
      case 'cabinet':
        return { label: lang === 'fi' ? 'Kabinetti' : 'Private Cabinet', icon: <Home size={14} color="#8b5cf6" /> };
      case 'buyout':
        return { label: lang === 'fi' ? 'Koko Ravintola (Buyout)' : 'Full Venue Buyout', icon: <Wine size={14} color="#e05a36" /> };
      default:
        return { label: lang === 'fi' ? 'Tilaisuus' : 'Event', icon: <Sparkles size={14} color="#f59e0b" /> };
    }
  };

  const getStatusBadge = (status: EventBooking['status']) => {
    switch (status) {
      case 'pending':
        return {
          bg: 'rgba(245, 158, 11, 0.15)',
          color: '#f59e0b',
          border: 'rgba(245, 158, 11, 0.3)',
          label: lang === 'fi' ? 'Käsittelyssä (Uusi)' : 'Pending Review',
          icon: <Clock size={13} />
        };
      case 'contacted':
        return {
          bg: 'rgba(59, 130, 246, 0.15)',
          color: '#3b82f6',
          border: 'rgba(59, 130, 246, 0.3)',
          label: lang === 'fi' ? 'Yhteyttä otettu' : 'Contacted',
          icon: <MessageSquare size={13} />
        };
      case 'confirmed':
        return {
          bg: 'rgba(16, 185, 129, 0.15)',
          color: '#10b981',
          border: 'rgba(16, 185, 129, 0.3)',
          label: lang === 'fi' ? 'Vahvistettu' : 'Confirmed',
          icon: <CheckCircle2 size={13} />
        };
      case 'declined':
        return {
          bg: 'rgba(239, 68, 68, 0.15)',
          color: '#ef4444',
          border: 'rgba(239, 68, 68, 0.3)',
          label: lang === 'fi' ? 'Peruutettu / Ei tilaa' : 'Declined',
          icon: <XCircle size={13} />
        };
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1300px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ background: 'rgba(224,90,54,0.12)', padding: '10px', borderRadius: '12px', color: 'var(--accent-copper)' }}>
              <CalendarCheck size={24} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                {lang === 'fi' ? 'Yksityistilaisuudet & Varaukset' : 'Event & Private Bookings'}
              </h1>
              <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                {lang === 'fi'
                  ? 'Hallinnoi verkkosivulta tulleita tapahtumakyselyitä ja kabinettivarauksia. Posti ohjataan osoitteeseen asiakaspalvelu@theovenstory.fi.'
                  : 'Manage private bookings, corporate dinners, and buyouts. Notifications routed to asiakaspalvelu@theovenstory.fi.'}
              </p>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={loadBookings}
            className="btn-secondary"
            title="Päivitä varaukset"
            style={{ padding: '9px 14px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <RefreshCw size={15} className={loading ? 'spin' : ''} />
            <span>Päivitä</span>
          </button>

          <button
            onClick={exportCSV}
            className="btn-secondary"
            title="Vie CSV"
            style={{ padding: '9px 14px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Download size={15} />
            <span>Vie CSV</span>
          </button>

          <button
            onClick={() => setIsAddModalOpen(true)}
            className="btn-primary"
            style={{ padding: '9px 18px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700 }}
          >
            <Plus size={16} />
            <span>{lang === 'fi' ? 'Uusi Varaus' : 'New Booking'}</span>
          </button>
        </div>
      </div>

      {/* Metric Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: '14px', padding: '18px' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
            {lang === 'fi' ? 'Kaikki Kyselyt' : 'Total Enquiries'}
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '6px' }}>
            {totalCount}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '4px' }}>
            {contactedCount} {lang === 'fi' ? 'käsittelyssä' : 'in progress'}
          </div>
        </div>

        <div style={{ background: 'var(--bg-card)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '14px', padding: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.8rem', color: '#f59e0b', fontWeight: 700, textTransform: 'uppercase' }}>
              {lang === 'fi' ? 'Odottaa Vastausta' : 'Needs Response'}
            </span>
            <Clock size={16} color="#f59e0b" />
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: '#f59e0b', marginTop: '6px' }}>
            {pendingCount}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '4px' }}>
            {lang === 'fi' ? 'Vastaa 24 tunnin sisällä' : 'Reply within 24h'}
          </div>
        </div>

        <div style={{ background: 'var(--bg-card)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '14px', padding: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: 700, textTransform: 'uppercase' }}>
              {lang === 'fi' ? 'Vahvistetut Tapahtumat' : 'Confirmed Events'}
            </span>
            <CheckCircle2 size={16} color="#10b981" />
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: '#10b981', marginTop: '6px' }}>
            {confirmedCount}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '4px' }}>
            {lang === 'fi' ? 'Sitovat varaukset' : 'Firm bookings'}
          </div>
        </div>

        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: '14px', padding: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--accent-copper)', fontWeight: 700, textTransform: 'uppercase' }}>
              {lang === 'fi' ? 'Vahvistetut Vieraat' : 'Expected Guests'}
            </span>
            <Users size={16} color="var(--accent-copper)" />
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--accent-copper)', marginTop: '6px' }}>
            {totalExpectedGuests}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '4px' }}>
            {lang === 'fi' ? 'Henkilöä tulossa' : 'Confirmed attendees'}
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: '14px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        {/* Status Tabs */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {(['all', 'pending', 'contacted', 'confirmed', 'declined'] as const).map(st => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              style={{
                background: statusFilter === st ? 'var(--accent-copper)' : 'rgba(255,255,255,0.04)',
                color: statusFilter === st ? '#ffffff' : 'var(--text-secondary)',
                border: '1px solid',
                borderColor: statusFilter === st ? 'var(--accent-copper)' : 'var(--border-subtle)',
                padding: '6px 14px',
                borderRadius: '8px',
                fontSize: '0.82rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              {st === 'all' && (lang === 'fi' ? `Kaikki (${totalCount})` : `All (${totalCount})`)}
              {st === 'pending' && (lang === 'fi' ? `Uudet (${pendingCount})` : `Pending (${pendingCount})`)}
              {st === 'contacted' && (lang === 'fi' ? `Käsittelyssä (${contactedCount})` : `Contacted (${contactedCount})`)}
              {st === 'confirmed' && (lang === 'fi' ? `Vahvistettu (${confirmedCount})` : `Confirmed (${confirmedCount})`)}
              {st === 'declined' && (lang === 'fi' ? 'Peruutettu' : 'Declined')}
            </button>
          ))}
        </div>

        {/* Search input & event type filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 320px', maxWidth: '480px' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={16} color="var(--text-muted)" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={lang === 'fi' ? 'Etsi nimellä, sähköpostilla tai puhelimella...' : 'Search by name, email, or phone...'}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '8px',
                color: 'var(--text-primary)',
                fontSize: '0.85rem'
              }}
            />
          </div>

          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '8px',
              color: 'var(--text-primary)',
              fontSize: '0.85rem'
            }}
          >
            <option value="all">{lang === 'fi' ? 'Kaikki tapahtumat' : 'All Event Types'}</option>
            <option value="birthday">{lang === 'fi' ? 'Syntymäpäivä' : 'Birthday'}</option>
            <option value="corporate">{lang === 'fi' ? 'Yritysjuhla' : 'Corporate'}</option>
            <option value="team">{lang === 'fi' ? 'Tiimi-ilta' : 'Team Dinner'}</option>
            <option value="cabinet">{lang === 'fi' ? 'Kabinetti' : 'Private Cabinet'}</option>
            <option value="buyout">{lang === 'fi' ? 'Koko Ravintola' : 'Buyout'}</option>
          </select>
        </div>
      </div>

      {/* Bookings List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
          <RefreshCw size={24} className="spin" style={{ marginBottom: '12px' }} />
          <div>Ladataan varauskyselyitä...</div>
        </div>
      ) : filteredBookings.length === 0 ? (
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: '16px',
            padding: '60px 20px',
            textAlign: 'center',
            color: 'var(--text-muted)'
          }}
        >
          <CalendarCheck size={48} style={{ opacity: 0.3, marginBottom: '12px' }} />
          <h3 style={{ margin: '0 0 6px 0', color: 'var(--text-primary)' }}>
            {lang === 'fi' ? 'Ei varauskyselyitä valituilla suodattimilla' : 'No enquiries found matching filters'}
          </h3>
          <p style={{ margin: 0, fontSize: '0.88rem' }}>
            {lang === 'fi' ? 'Kaikki uudet verkkosivulta lähetetyt kyselyt ilmestyvät tähän.' : 'New landing page event requests will appear here in real time.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {filteredBookings.map(booking => {
            const statusInfo = getStatusBadge(booking.status);
            const typeInfo = getEventTypeLabel(booking.eventType);

            return (
              <div
                key={booking.id}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '14px',
                  padding: '20px',
                  transition: 'all 0.2s ease',
                  boxShadow: booking.status === 'pending' ? '0 0 15px rgba(245,158,11,0.06)' : 'none'
                }}
              >
                {/* Top Row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                      {booking.fullName}
                    </div>

                    {/* Event Type Badge */}
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'rgba(255,255,255,0.06)',
                        padding: '4px 10px',
                        borderRadius: '999px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        color: 'var(--text-secondary)'
                      }}
                    >
                      {typeInfo.icon}
                      <span>{typeInfo.label}</span>
                    </div>

                    {/* Guests & Date */}
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'rgba(224,90,54,0.1)',
                        padding: '4px 10px',
                        borderRadius: '999px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        color: 'var(--accent-copper)'
                      }}
                    >
                      <Users size={13} />
                      <span>{booking.guests} {lang === 'fi' ? 'henkilöä' : 'guests'}</span>
                    </div>

                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'rgba(245,158,11,0.1)',
                        padding: '4px 10px',
                        borderRadius: '999px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        color: 'var(--accent-amber)'
                      }}
                    >
                      <CalendarCheck size={13} />
                      <span>{booking.preferredDate}</span>
                    </div>
                  </div>

                  {/* Status Dropdown */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: statusInfo.bg,
                        color: statusInfo.color,
                        border: `1px solid ${statusInfo.border}`,
                        padding: '5px 12px',
                        borderRadius: '8px',
                        fontSize: '0.8rem',
                        fontWeight: 700
                      }}
                    >
                      {statusInfo.icon}
                      <span>{statusInfo.label}</span>
                    </div>

                    <select
                      value={booking.status}
                      onChange={e => handleStatusChange(booking.id, e.target.value as any)}
                      style={{
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border-medium)',
                        color: 'var(--text-primary)',
                        padding: '5px 10px',
                        borderRadius: '8px',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      <option value="pending">🟡 {lang === 'fi' ? 'Käsittelyssä' : 'Pending'}</option>
                      <option value="contacted">🔵 {lang === 'fi' ? 'Yhteyttä otettu' : 'Contacted'}</option>
                      <option value="confirmed">🟢 {lang === 'fi' ? 'Vahvistettu' : 'Confirmed'}</option>
                      <option value="declined">🔴 {lang === 'fi' ? 'Peruutettu' : 'Declined'}</option>
                    </select>
                  </div>
                </div>

                {/* Contact and Message Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '14px' }}>
                  {/* Contact Info */}
                  <div style={{ background: 'var(--bg-primary)', borderRadius: '10px', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
                      <Phone size={14} color="var(--accent-copper)" />
                      <a href={`tel:${booking.phone}`} style={{ color: 'var(--text-primary)', textDecoration: 'none', fontWeight: 600 }}>
                        {booking.phone}
                      </a>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
                      <Mail size={14} color="var(--accent-amber)" />
                      <a href={`mailto:${booking.email}`} style={{ color: 'var(--text-primary)', textDecoration: 'none' }}>
                        {booking.email}
                      </a>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      Saapunut: {formatDateTime(booking.submittedAt)}
                    </div>
                  </div>

                  {/* Customer Message / Special Wishes */}
                  <div style={{ background: 'var(--bg-primary)', borderRadius: '10px', padding: '12px 16px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '4px' }}>
                      {lang === 'fi' ? 'Asiakkaan toiveet & viesti' : 'Customer Message & Wishes'}
                    </div>
                    <p style={{ margin: 0, fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                      {booking.message || (lang === 'fi' ? 'Ei erillisiä toiveita ilmoitettu.' : 'No special notes provided.')}
                    </p>
                  </div>
                </div>

                {/* Staff Internal Notes & Action Row */}
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px', borderTop: '1px solid var(--border-subtle)', paddingTop: '12px' }}>
                  {/* Internal Staff Notes */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 360px' }}>
                    <Edit3 size={14} color="var(--text-muted)" />
                    <input
                      type="text"
                      defaultValue={booking.staffNotes || ''}
                      placeholder={lang === 'fi' ? 'Henkilökunnan muistiinpano (esim. menu nro 2 sovittu, soita uudelleen 12.10)...' : 'Add internal staff notes...'}
                      onBlur={e => handleUpdateNotes(booking.id, e.target.value)}
                      style={{
                        flex: 1,
                        background: 'transparent',
                        border: '1px dashed var(--border-subtle)',
                        borderRadius: '6px',
                        padding: '6px 10px',
                        fontSize: '0.82rem',
                        color: 'var(--text-primary)'
                      }}
                    />
                  </div>

                  {/* Actions: Quick Email Reply & Delete */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => openEmailReply(booking)}
                      className="btn-secondary"
                      style={{
                        padding: '6px 14px',
                        borderRadius: '8px',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: 'var(--accent-copper)'
                      }}
                      title="Avaa esitäytetty sähköpostivastaus asiakkaalle"
                    >
                      <Send size={13} />
                      <span>{lang === 'fi' ? 'Vastaa Sähköpostilla' : 'Quick Email Reply'}</span>
                    </button>

                    <button
                      onClick={() => handleDelete(booking.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-dim)',
                        cursor: 'pointer',
                        padding: '6px',
                        borderRadius: '6px'
                      }}
                      title="Poista kysely"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Manual New Booking Modal */}
      {isAddModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(5px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setIsAddModalOpen(false)}
        >
          <div
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-medium)',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '560px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-primary)' }}>
              {lang === 'fi' ? 'Kirjaa Uusi Yksityistilaisuus / Pöytävaraus' : 'Record New Event / Private Booking'}
            </h2>
            <p style={{ margin: '0 0 20px 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              {lang === 'fi' ? 'Lisää puhelimitse tai paikan päällä sovittu tapahtuma suoraan järjestelmään.' : 'Manually record a phone or walk-in event reservation.'}
            </p>

            <form onSubmit={handleCreateBooking}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  {lang === 'fi' ? 'Asiakkaan Nimi' : 'Customer Name'} *
                </label>
                <input
                  type="text"
                  required
                  value={newBooking.fullName}
                  onChange={e => setNewBooking({ ...newBooking, fullName: e.target.value })}
                  className="os-input"
                  placeholder="Esim. Juha Mäkinen"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={newBooking.email}
                    onChange={e => setNewBooking({ ...newBooking, email: e.target.value })}
                    className="os-input"
                    placeholder="juha@firma.fi"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Puhelin *
                  </label>
                  <input
                    type="tel"
                    required
                    value={newBooking.phone}
                    onChange={e => setNewBooking({ ...newBooking, phone: e.target.value })}
                    className="os-input"
                    placeholder="+358 40 123 4567"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    {lang === 'fi' ? 'Tilaisuuden Tyyppi' : 'Event Type'}
                  </label>
                  <select
                    value={newBooking.eventType}
                    onChange={e => setNewBooking({ ...newBooking, eventType: e.target.value as any })}
                    className="os-select"
                  >
                    <option value="birthday">Syntymäpäivä / Birthday</option>
                    <option value="corporate">Yritysjuhla / Corporate</option>
                    <option value="team">Tiimi-ilta / Team Dinner</option>
                    <option value="cabinet">Kabinetti / Cabinet</option>
                    <option value="buyout">Koko Ravintola / Buyout</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    {lang === 'fi' ? 'Henkilömäärä' : 'Guests'} *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    required
                    value={newBooking.guests}
                    onChange={e => setNewBooking({ ...newBooking, guests: Number(e.target.value) })}
                    className="os-input"
                  />
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  {lang === 'fi' ? 'Päivämäärä' : 'Event Date'} *
                </label>
                <input
                  type="date"
                  required
                  value={newBooking.preferredDate}
                  onChange={e => setNewBooking({ ...newBooking, preferredDate: e.target.value })}
                  className="os-input"
                />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  {lang === 'fi' ? 'Toiveet ja Menukokonaisuus' : 'Wishes and Menu details'}
                </label>
                <textarea
                  rows={2}
                  value={newBooking.message}
                  onChange={e => setNewBooking({ ...newBooking, message: e.target.value })}
                  className="os-textarea"
                  placeholder="Puu-uunipizzat, alkoholiton juomapaketti..."
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  {lang === 'fi' ? 'Sisäinen Muistiinpano' : 'Internal Staff Notes'}
                </label>
                <input
                  type="text"
                  value={newBooking.staffNotes}
                  onChange={e => setNewBooking({ ...newBooking, staffNotes: e.target.value })}
                  className="os-input"
                  placeholder="Pöydät 1-4 varattu kabinetista, lasku firman nimeen..."
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="btn-secondary"
                  style={{ padding: '10px 18px', borderRadius: '10px' }}
                >
                  Peruuta
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ padding: '10px 22px', borderRadius: '10px', fontWeight: 700 }}
                >
                  Tallenna Varaus
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
